import { useLayoutEffect, useRef, useState } from 'react';

// ─── Vertical placement for popovers anchored to a trigger ───────────────────
// Shared by every anchored panel that hangs off a row/button — the `⋮` OverflowMenu,
// the backlog state/assignee pickers, the status menus, the Smartguides label
// popovers, the sidebar connection details — so they can't drift apart. Anchoring
// downwards unconditionally is what b-01736 was: a menu on a row near the bottom of
// the viewport rendered past the fold, and its lower entries couldn't be reached — a
// `maxHeight` can't rescue that, because the panel's TOP is already too low. b-01764
// brought the rest of the family here.
//
// The caller owns horizontal placement (right-aligned, left-clamped, centred — they
// all differ) and the open/close lifecycle; this owns only the vertical decision.
// `useAnchoredVertical` is the usual entry point; `verticalPlacement` is there for a
// caller that already runs its own measure/reposition loop (MenuOrganizer does).

/** Where an anchored panel should sit vertically. `top` XOR `bottom` is set:
 *  opening upward is expressed as a `bottom` offset so the panel grows away from
 *  the anchor without the caller having to know its height. */
export interface VerticalPlacement {
  /** True when the panel opens UPWARD (use `bottom`); false → downward (`top`). */
  up: boolean;
  top?: number;
  bottom?: number;
  /** Free space on the chosen side. Apply it as `maxHeight` together with
   *  `overflowY: 'auto'` and the panel can never spill past a viewport edge.
   *  DON'T apply it to a panel with absolutely-positioned children that are meant to
   *  escape its box (a type-ahead dropdown, say) — making the panel a scroll
   *  container clips them on both axes. Such a panel should stay uncapped and rely
   *  on the flip alone. */
  maxHeight: number;
}

/** Below this much room on both sides, anchoring stops being meaningful — see the
 *  degenerate branch in `verticalPlacement`. */
const MIN_USABLE = 80;

export interface VerticalPlacementOptions {
  /** Space between the anchor and the panel. */
  gap?: number;
  /** Space kept clear at the viewport edge. */
  margin?: number;
}

/**
 * Place a panel of `panelHeight` (its natural, unclamped height — read
 * `el.scrollHeight`, which an applied `maxHeight` does not shrink) against `anchor`.
 *
 * Below when it fits, flipped above when it doesn't, and when NEITHER side can hold
 * it the roomier side wins and the cap makes it scroll internally instead of
 * overflowing. A panel measured as 0 (the first, pre-measurement render) simply
 * "fits" below, which is the placement it would have had anyway.
 */
export function verticalPlacement(
  anchor: { top: number; bottom: number },
  panelHeight: number,
  { gap = 6, margin = 8 }: VerticalPlacementOptions = {},
): VerticalPlacement {
  const viewportH = window.innerHeight;
  const roomBelow = viewportH - margin - (anchor.bottom + gap);
  const roomAbove = anchor.top - gap - margin;

  const down = { up: false as const, top: Math.round(anchor.bottom + gap) };
  // `bottom` is measured from the viewport's bottom edge, so this puts the panel's
  // lower edge `gap` above the anchor's top — and the panel then grows upward on its
  // own, which is why the caller never has to pin a height.
  const upward = { up: true as const, bottom: Math.round(viewportH - anchor.top + gap) };

  if (panelHeight <= roomBelow) return { ...down, maxHeight: Math.round(roomBelow) };
  if (panelHeight <= roomAbove) return { ...upward, maxHeight: Math.round(roomAbove) };

  // Degenerate: the anchor leaves no usable room on EITHER side (a viewport barely
  // taller than the trigger itself). Anchoring would mean either a zero-height,
  // invisible panel or a cap that pushes the top edge off screen — so stop anchoring
  // and fill the viewport between the margins instead.
  if (Math.max(roomAbove, roomBelow) < MIN_USABLE) {
    return { up: false, top: margin, maxHeight: Math.max(0, Math.round(viewportH - margin * 2)) };
  }

  // Neither side fits: take the roomier one and let the panel scroll inside it. The cap
  // is the REAL room and never a comfort floor — overshooting it upward would push the
  // panel's top edge off screen, and content scrolled past the top of the viewport
  // can't be scrolled back into view the way bottom overflow can.
  const room = Math.round(Math.max(roomAbove, roomBelow));
  return roomAbove > roomBelow ? { ...upward, maxHeight: room } : { ...down, maxHeight: room };
}

/**
 * The measure-then-place dance for a portal panel: attach `panelRef` to it, pass the
 * trigger's viewport rect while the panel is open (`null` when closed), and spread
 * `place` into the panel's style — `top` or `bottom`, plus `maxHeight` with
 * `overflowY: 'auto'`.
 *
 * The panel's height can't be known before it renders, so the first render gets the
 * provisional downward placement and the refinement happens in a layout effect, which
 * React flushes BEFORE the browser paints — the provisional spot is never visible, and
 * if the measurement somehow never ran, the fallback is the old below-the-trigger
 * placement rather than an invisible panel.
 *
 * The panel is re-measured by a `ResizeObserver`, not just once on open: a menu that
 * opens on a cold cache showing "Loading…" would otherwise be placed against a 30px
 * panel and never reconsidered when its rows arrive — which is the flip this exists
 * for. The height is measured as `scrollHeight` (the natural content height, which the
 * `maxHeight` we hand back does not shrink), so the loop settles instead of
 * oscillating between capped and uncapped measurements.
 *
 * `place` is derived from the current `anchor` on every render rather than stored, so a
 * caller that hands over a NEW rect gets the matching placement in the same render
 * rather than one render late. It does not un-stale the rect itself — the callers here
 * capture it when the panel opens and either close on scroll/resize or accept the drift.
 */
export function useAnchoredVertical<T extends HTMLElement = HTMLDivElement>(
  anchor: DOMRect | null,
  opts?: VerticalPlacementOptions,
): { panelRef: React.RefObject<T | null>; place: VerticalPlacement | null } {
  const panelRef = useRef<T | null>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = anchor ? panelRef.current : null;
    const measure = () => setHeight((prev) => {
      const next = el?.scrollHeight ?? 0;
      return prev === next ? prev : next;
    });
    measure();
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [anchor]);

  return { panelRef, place: anchor ? verticalPlacement(anchor, height, opts) : null };
}
